/* eslint-disable no-console */
import React from 'react';
import {
  LineChart as RechartLine,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

import {
  formatNumber,
  returnMaxAndMin
} from '../../../pages/netZero/utils/commonFunctions';
import {formatIndian} from '../../../utils/formatIndian';
import {eachWordCapOrAllCap} from '../../../utils/FormUtils/FirstCap';
import {divideIntoSixParts} from '../BarChart/utils/commonFunctions';
import CompositeTooltipLabel from '../CompositeChart/utils/CompositeTooltipLabel';
import DotRender from '../CompositeChart/utils/DotRender';

import {filterLineData} from './utils/commonFunctions';

const LineChart: React.FC<{
  data: any[];
  mainData: any;
  minMax?: boolean;
  isCost?: boolean;
}> = ({data, mainData, minMax = false, isCost = false}) => {
  if (
    data?.every((item: any) => {
      return item?.value === 0 || typeof item?.value !== 'number';
    })
  ) {
    data = [];
  }
  const dataSet = filterLineData(data);
  const checkMax = Math.ceil(Math.max(...data.map((item) => item.value)) * 1.2);
  const mainMax = Number.isFinite(checkMax) ? checkMax : 100;
  const divideIntoSix = divideIntoSixParts(mainMax);
  const divideIntoSixFormat = divideIntoSixParts(mainMax).map((item) => {
    return `${formatNumber(item)} ${data?.[0]?.unit ?? ''}`.length;
  });

  const {max, min} = returnMaxAndMin(data);

  return divideIntoSix.every((item) => {
    return item >= 0;
  }) ? (
    <ResponsiveContainer width="100%" height="100%">
      <RechartLine data={data} margin={{top: 0, bottom: 0, left: 0, right: 0}}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="month"
          allowDuplicatedCategory={false}
          axisLine={false}
          tickLine={false}
          label={{position: 'insideBottomRight'}}
          interval={0}
          padding={{left: 60, right: 60}}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          domain={[0, mainMax]}
          style={{font: 'normal normal 400 12px/15px Montserrat'}}
          ticks={divideIntoSix}
          tickFormatter={(value) => {
            return isCost
              ? `${data?.[0]?.unit ?? ''} ${formatNumber(value)}`
              : `${formatNumber(value)} ${data?.[0]?.unit ?? ''}`;
          }}
          width={Math.max(...divideIntoSixFormat) * 8}
        />
        <Tooltip
          cursor={{
            stroke:
              dataSet.length > 0
                ? mainData?.fill && mainData?.fill !== 'NA'
                  ? mainData?.fill
                  : '#1890FF'
                : undefined,
            strokeWidth: 2,
            strokeDasharray: '5 5'
          }}
          content={(props) => (
            <CompositeTooltipLabel
              {...props}
              isSingle={false}
              label={mainData?.label ?? ''}
              dataSet={dataSet}
              isCost={isCost}
            />
          )}
        />
        {dataSet.length >= 2 && (
          <Legend
            verticalAlign="top"
            iconType="circle"
            align="right"
            formatter={(value) => (
              <span style={{color: 'black'}}>{eachWordCapOrAllCap(value)}</span>
            )}
            wrapperStyle={{
              paddingBottom: '1rem'
            }}
          />
        )}
        {dataSet?.map((item: any, index: number) => {
          return (
            <Line
              key={index}
              name={item?.[0]?.source}
              dataKey="value"
              stroke={item?.[0].fill ?? mainData?.fill}
              data={item}
              strokeWidth={3}
              activeDot={(props: any) => <DotRender payload={props} />}
              dot={{r: 3, strokeWidth: 6}}
            />
          );
        })}
        {minMax && max?.value !== min?.value && (
          <>
            <ReferenceLine
              y={max?.value ?? undefined}
              label={{
                // content: (props) => {
                //   return <LabelOverflow label="Peak" labelProps={props} />;
                // },
                value: `Peak at ${formatIndian(max?.value)} ${
                  data?.[0]?.unit ?? ''
                }`,
                position: 'top'
              }}
              stroke="#FA6B84"
              strokeDasharray="3 3"
            />
            <ReferenceLine
              y={min?.value ?? undefined}
              label={{
                // content: (props) => (
                //   <LabelOverflow label="Off-Peak" labelProps={props} />
                // ),
                value: `Off-Peak at ${formatIndian(min?.value)} ${
                  data?.[0]?.unit ?? ''
                }`,
                position: 'bottom'
              }}
              stroke="#6aa84f"
              strokeDasharray="3 3"
            />
          </>
        )}
      </RechartLine>
    </ResponsiveContainer>
  ) : (
    <></>
  );
};

export default LineChart;
